import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Car, UserPlus, Share2, Wallet, Ban, BarChart3, HeadphonesIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { icon: UserPlus, num: "Pasul 1", title: "Inscrie-te gratuit", desc: "Creaza-ti un cont de afiliat AutoPro Daune, primesti un cod unic de recomandare si incepi sa castigi." },
  { icon: Share2, num: "Pasul 2", title: "Recomanda-ne", desc: "Trimite codul tau de recomandare prietenilor, familiei sau urmaritorilor tai care au nevoie de ajutor cu un dosar de dauna." },
  { icon: Wallet, num: "Pasul 3", title: "Esti platit", desc: "Primesti comision pentru fiecare dosar finalizat cu succes. Platile se fac lunar, transparent si la timp." }
];

const BENEFITS = [
  { icon: Ban, title: "Fara limita, fara conditii", desc: "Nu exista plafon maxim de comisioane. Cu cat recomanzi mai mult, cu atat castigi mai mult." },
  { icon: BarChart3, title: "Urmarire in timp real", desc: "Panou dedicat de afiliat pentru monitorizarea dosarelor, statusurilor si comisioanelor tale in orice moment." },
  { icon: HeadphonesIcon, title: "Suport dedicat", desc: "Ai un partener real alaturi de tine. Te ajutam cu orice intrebare si te ghidam pe tot parcursul colaborarii." }
];

const AffiliateRegister = () => {
  const navigate = useNavigate();
  const { loginAfiliat } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nume: "", prenume: "", email: "", telefon: "", parola: "" });
  const [passwordError, setPasswordError] = useState("");

  const validatePassword = (password) => {
    if (password.length < 8) return "Minim 8 caractere";
    if (!/[A-Z]/.test(password)) return "O literă mare";
    if (!/[a-z]/.test(password)) return "O literă mică";
    if (!/\d/.test(password)) return "O cifră";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwordError) { toast.error("Parola nu îndeplinește cerințele"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/affiliate/register`, form);
      loginAfiliat(res.data.token, res.data.afiliat);
      toast.success("Cont creat cu succes!");
      navigate("/affiliate/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la înregistrare");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
            <Car className="w-6 h-6 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg text-gray-900 leading-tight">AUTO PRO</span>
            <span className="text-xs text-emerald-600 font-medium -mt-1">DAUNE</span>
          </div>
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Programul de Afiliere AutoPro Daune</h1>
            <p className="text-gray-600 mb-8">Castiga comision pentru fiecare persoana pe care o recomanzi si care finalizeaza un dosar de dauna cu noi. Fara investitie, fara risc.</p>

            <div className="mb-10">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-6">Fara stres, in doar 3 pasi simpli!</p>
              <div className="grid gap-6">
                {STEPS.map(s => (
                  <div key={s.num} className="flex gap-4" data-testid={`step-${s.num}`}>
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">{s.num}</p>
                      <h3 className="font-bold text-gray-900">{s.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-5">De ce sa te alaturi?</h2>
              <div className="grid gap-4">
                {BENEFITS.map(b => (
                  <div key={b.title} className="flex gap-4 p-4 bg-white rounded-xl border border-gray-100" data-testid={`benefit-${b.title}`}>
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <b.icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">{b.title}</h4>
                      <p className="text-xs text-gray-600 mt-1">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Devino Afiliat</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prenume</label>
                <input type="text" value={form.prenume} onChange={(e) => setForm({ ...form, prenume: e.target.value })} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" data-testid="affiliate-register-prenume" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nume</label>
                <input type="text" value={form.nume} onChange={(e) => setForm({ ...form, nume: e.target.value })} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" data-testid="affiliate-register-nume" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" data-testid="affiliate-register-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input type="tel" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} required pattern="^07[0-9]{8}$" title="Numar de telefon valid (ex: 07XX XXX XXX)" placeholder="07XX XXX XXX" className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" data-testid="affiliate-register-telefon" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parolă</label>
              <input type="password" value={form.parola} onChange={(e) => { setForm({ ...form, parola: e.target.value }); setPasswordError(validatePassword(e.target.value)); }} required className={`w-full h-12 px-4 rounded-xl border bg-gray-50 focus:bg-white ${passwordError && form.parola ? "border-red-300" : "border-gray-200"}`} data-testid="affiliate-register-password" />
              {form.parola && <p className={`text-xs mt-1 ${passwordError ? "text-red-500" : "text-emerald-600"}`}>{passwordError || "✓ Parolă sigură"}</p>}
              <p className="text-xs text-gray-400 mt-1">Minim 8 caractere, literă mare, literă mică, cifră</p>
            </div>
            <button type="submit" disabled={loading || !!passwordError} className="w-full h-12 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 disabled:bg-emerald-300" data-testid="affiliate-register-submit">
              {loading ? "Se creează contul..." : "Creează Cont"}
            </button>
          </form>

          <p className="text-center text-gray-500 mt-6">
            Ai deja cont? <Link to="/affiliate/login" className="text-emerald-600 hover:text-emerald-700 font-medium">Conecteaza-te</Link>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateRegister;
